import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { scanError, useStartScan } from '../components/site.js';
import { Button, Card, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';

type AiVisibility = Awaited<ReturnType<typeof loadAiVisibility>>;
type Status = AiVisibility['checks'][number]['status'];
type Purpose = AiVisibility['bots'][number]['purpose'];

const loadAiVisibility = (siteId: string) =>
  unwrap(api.GET('/sites/{siteId}/ai-visibility', { params: { path: { siteId } } }));

const CHIP: Record<Status, string> = {
  ok: 'bg-green-100 text-green-900',
  warn: 'bg-amber-100 text-amber-950',
  bad: 'bg-red-100 text-red-900',
  unknown: 'bg-slate-100 text-slate-800',
};
const MARK: Record<Status, string> = { ok: '✓', warn: '!', bad: '✕', unknown: '?' };
/** Text of a check per status: what is fine, what could be better, what is wrong. */
const TEXT = { ok: 'ok', warn: 'warn', bad: 'why' } as const;

function scoreStyle(score: number) {
  if (score >= 80) return { bar: 'bg-green-700', text: 'text-green-800', key: 'good' } as const;
  if (score >= 50) return { bar: 'bg-amber-500', text: 'text-amber-800', key: 'medium' } as const;
  return { bar: 'bg-red-600', text: 'text-red-800', key: 'weak' } as const;
}

/** Visibility in AI search (T-42): can ChatGPT, Perplexity, Copilot and Claude read and cite the site? */
export function AiSearch() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'seo_home'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'seo_home', limit: 1 } },
        }),
      ),
    refetchInterval: (query) => {
      const s = query.state.data?.items[0]?.status;
      return s === 'queued' || s === 'running' ? 5000 : false;
    },
  });
  const latestScan = scans.data?.items[0];
  const running = latestScan?.status === 'queued' || latestScan?.status === 'running';
  const query = useQuery({
    queryKey: ['site', siteId, 'ai-visibility', latestScan?.status],
    queryFn: () => loadAiVisibility(siteId),
  });
  const start = useStartScan(siteId, 'seo_home');

  const data = query.data;
  const style = data?.score != null ? scoreStyle(data.score) : null;
  const byPurpose = (purpose: Purpose) => data?.bots.filter((b) => b.purpose === purpose) ?? [];

  return (
    <>
      <PageTitle
        actions={
          <Button busy={start.isPending} disabled={running} onClick={() => start.mutate()}>
            {t('ai.check')}
          </Button>
        }
      >
        {t('ai.title')}
      </PageTitle>
      <p className="-mt-4 mb-6 max-w-prose text-slate-700">{t('ai.intro')}</p>
      <div className="mb-4 flex flex-col gap-3">
        {start.isError ? <Notice kind="error">{scanError(start.error, t)}</Notice> : null}
        {running ? <Notice kind="info">{t('ai.running')}</Notice> : null}
      </div>
      {query.isPending ? <Loading /> : null}
      {query.isError ? (
        <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
      ) : null}
      {data && data.checkedAt === null ? <Notice kind="info">{t('ai.none')}</Notice> : null}

      {data && data.checkedAt !== null ? (
        <>
          <Card className="mb-6 flex flex-col gap-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{t('ai.scoreTitle')}</h2>
                <p className="text-sm text-slate-700">
                  {t('ai.checkedAt', { time: formatDateTime(data.checkedAt, i18n.language) })}
                </p>
              </div>
              {data.score !== null && style ? (
                <p className={`text-4xl font-bold tabular-nums ${style.text}`}>
                  {data.score}
                  <span className="text-lg font-semibold text-slate-600"> / 100</span>
                </p>
              ) : null}
            </div>
            {data.score !== null && style ? (
              <>
                <div
                  className="h-3 rounded-full bg-slate-100"
                  role="img"
                  aria-label={t('ai.scoreLabel', { score: data.score })}
                >
                  <div
                    className={`h-3 rounded-full ${style.bar}`}
                    style={{ width: `${Math.max(2, data.score)}%` }}
                  />
                </div>
                <p className="font-semibold">{t(`ai.verdict.${style.key}`)}</p>
              </>
            ) : null}
          </Card>

          <h2 className="mb-3 text-xl font-semibold">{t('ai.checksTitle')}</h2>
          <ul className="mb-8 flex flex-col gap-3">
            {data.checks.map((c) => (
              <li key={c.key}>
                <Card className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
                  <span
                    className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold sm:w-44 ${CHIP[c.status]}`}
                  >
                    <span aria-hidden="true">{MARK[c.status]}</span>
                    {t(`ai.status.${c.status}`)}
                  </span>
                  <div className="flex flex-col gap-1">
                    <h3 className="font-semibold">{t(`ai.checks.${c.key}.title`)}</h3>
                    <p className="text-slate-700">
                      {c.status === 'unknown'
                        ? t('ai.unknownText')
                        : t(`ai.checks.${c.key}.${TEXT[c.status]}`, c.detail)}
                    </p>
                    {c.status === 'warn' || c.status === 'bad' ? (
                      <p>
                        <span className="font-semibold">{t('ai.todo')}</span>{' '}
                        {t(`ai.checks.${c.key}.todo`, c.detail)}
                      </p>
                    ) : null}
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {data ? (
        <>
          <h2 className="mb-1 text-xl font-semibold">{t('ai.botsTitle')}</h2>
          <p className="mb-3 max-w-prose text-slate-700">{t('ai.botsIntro')}</p>
          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            {(['search', 'assistant', 'training'] as const).map((purpose) => (
              <Card key={purpose} className="flex flex-col gap-3">
                <div>
                  <h3 className="font-semibold">{t(`ai.purpose.${purpose}.title`)}</h3>
                  <p className="text-sm text-slate-700">{t(`ai.purpose.${purpose}.text`)}</p>
                </div>
                <ul className="flex flex-col divide-y divide-slate-200">
                  {byPurpose(purpose).map((b) => (
                    <li key={b.token} className="flex items-center justify-between gap-3 py-2">
                      <span className="flex min-w-0 flex-col">
                        <span className="font-mono text-sm font-semibold break-all">{b.token}</span>
                        <span className="text-sm text-slate-700">{b.product}</span>
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-sm font-semibold ${
                          b.allowed === null
                            ? CHIP.unknown
                            : b.allowed
                              ? CHIP.ok
                              : purpose === 'training'
                                ? CHIP.unknown
                                : CHIP.bad
                        }`}
                      >
                        {b.allowed === null
                          ? t('ai.bot.unknown')
                          : b.allowed
                            ? t('ai.bot.allowed')
                            : t('ai.bot.blocked')}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <Card className="mb-6 flex flex-col gap-2 border-slate-200 bg-slate-50">
            <h2 className="text-lg font-semibold">{t('ai.howTitle')}</h2>
            <p className="text-slate-800">{t('ai.how')}</p>
            <p className="text-slate-800">{t('ai.firewall')}</p>
          </Card>
          <p className="text-sm text-slate-700">
            {t('ai.findingsHint')}{' '}
            <LinkButton to={`/sites/${siteId}/findings`} variant="soft">
              {t('siteNav.findings')}
            </LinkButton>
          </p>
        </>
      ) : null}
    </>
  );
}
